import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { Collapsible } from '@sdk/components/Collapsible/Collapsible';
import { Feature } from '@sdk/common/features';
import { AutomatedBreakType } from '@sdk/settings/types';

import { AutomatedBreaksSettingsFormFields } from '@web/settings/components/schedule/Breaks/breaks.types';
import PremiumGuard from '@web/premiumGuard/PremiumGuard';
import usePremiumGuard from '@web/premiumGuard/usePremiumGuard';
import { PremiumModalNames } from '@web/premiumGuard/constants/modalNames';
import getPremiumTooltipId from '@web/premiumGuard/getPremiumTooltipId';
import SettingsSwitcherRow from '@web/settings/components/SettingsSwitcherRow';
import AutomatedBreak from '@web/settings/components/schedule/Breaks/AutomatedBreak/AutomatedBreak';
import { useAutomatedBreakContentFields } from '@web/settings/components/schedule/Breaks/AutomatedBreaksContent/automatedBreaksContent.hooks';
import { useIsAttestationsFeatureFlagEnabled } from '@web/automatedBreaks/hooks/useIsAttestationsFeatureFlagEnabled';

type Props = {
  fields: AutomatedBreaksSettingsFormFields;
};

const AutomatedBreaksContent = ({ fields }: Props) => {
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { updateAttestation, updateMealBreakEnabled, updateRestBreakEnabled } =
    useAutomatedBreakContentFields(fields);
  const isAttestationFeatureFlagEnabled = useIsAttestationsFeatureFlagEnabled();

  return (
    <Column>
      {isAttestationFeatureFlagEnabled && (
        <PremiumGuard
          premiumGuardModalName={PremiumModalNames.breaksAttestation}
          hasAccess={isOrgFeatureEnabled(Feature.breaksAttestation)}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.breaksAttestation)]}>
          <SettingsSwitcherRow
            mTop={20}
            title={gettext('Attestation')}
            subtitle={gettext('Enable employees to attest whether they took the required breaks.')}
            value={fields.isAttestation.value}
            onChange={updateAttestation}
            disabled={!currentUser.isAdmin}
            premiumGuard={getPremiumGuardConfig(Feature.breaksAttestation)}
          />
        </PremiumGuard>
      )}

      <Row mTop={20} mBottom={20}>
        <HorizontalSeparator />
      </Row>

      <Collapsible isActive={fields.meal.isEnabled.value}>
        <SettingsSwitcherRow
          title={gettext('Meal break')}
          subtitle={gettext('Enable employees to clock out for a meal break.')}
          value={fields.meal.isEnabled.value}
          onChange={updateMealBreakEnabled}
          disabled={!currentUser.isAdmin}
        />
        <Collapsible.Content>
          <AutomatedBreak fields={fields.meal} automatedBreakType={AutomatedBreakType.meal} />
        </Collapsible.Content>
      </Collapsible>

      <Row mTop={20} mBottom={20}>
        <HorizontalSeparator />
      </Row>

      <Collapsible isActive={fields.rest.isEnabled.value}>
        <SettingsSwitcherRow
          title={gettext('Rest break')}
          subtitle={gettext('Enable employees to clock out for a rest break.')}
          value={fields.rest.isEnabled.value}
          onChange={updateRestBreakEnabled}
          disabled={!currentUser.isAdmin}
        />
        <Collapsible.Content>
          <AutomatedBreak fields={fields.rest} automatedBreakType={AutomatedBreakType.rest} />
        </Collapsible.Content>
      </Collapsible>

      <Row mBottom={20}></Row>
    </Column>
  );
};

export default AutomatedBreaksContent;
